import React from 'react';
import {
  bucketOptionsFor,
  RECOGNITION_BUCKET_OPTIONS,
  RECOGNITION_ISSUE_BUCKETS,
  RECOGNITION_OTHER_BUCKET,
} from '../../constants/recognitionReview';

/** Level one: drop the primary bucket entirely. Free at both levels of the taxonomy. */
export const BUCKET_PICKER_CLEAR_KEY = 'X';

/** 1 = top-level buckets, 2 = the sub-buckets of one top-level bucket. */
export type BucketPickerLevel = 1 | 2;

export type BucketPickerAction =
  | { kind: 'bucket'; path: string }
  | { kind: 'subBucket'; path: string }
  | { kind: 'clear' };

/**
 * Resolve one keystroke against the bucket taxonomy for the level it was typed at.
 *
 * Shortcut letters are unique within a level but repeat across levels, so the level - and, at level
 * two, the top-level bucket already armed - decides what a letter means.
 *
 * @param level Level the key was pressed at.
 * @param key The pressed key; case is ignored.
 * @param prefix The armed top-level bucket path; required at level two, ignored at level one.
 * @returns The action the key selects, or `null` when the level has no option for it.
 */
export function bucketActionFromShortcutKey(
  level: BucketPickerLevel,
  key: string,
  prefix?: string | null
): BucketPickerAction | null {
  const normalizedKey = key.toUpperCase();
  if (level === 1) {
    if (normalizedKey === BUCKET_PICKER_CLEAR_KEY) return { kind: 'clear' };
    const option = RECOGNITION_BUCKET_OPTIONS.find(
      (candidate) => candidate.shortcutKey === normalizedKey
    );
    return option ? { kind: 'bucket', path: option.path } : null;
  }
  const option = bucketOptionsFor(prefix ?? '').find(
    (candidate) => candidate.shortcutKey === normalizedKey
  );
  return option ? { kind: 'subBucket', path: option.path } : null;
}

/**
 * The Recognition issue bucket taxonomy, rendered inline and always open.
 *
 * Every category shows all of its leaves, so choosing a cause is one click with nothing to open
 * first - the picker used to be a modal behind a "Primary bucket" button, which put two extra
 * actions in front of the single most-used control in the inspector. Each option carries its
 * one-sentence definition as a tooltip, because neighbouring leaves ("Design group missing /
 * Partial" versus "Design group wrong / Partial") are not separable from their labels alone.
 *
 * The keyboard flow is unchanged and lives in the page that owns the page-level shortcuts: `B` arms
 * the picker, a category letter arms that category (the rest of the grid dims), and the sub letter
 * picks the leaf. This component only renders that state.
 */
export const RecognitionBucketPicker: React.FC<{
  selectedPath?: string | null;
  /** The category armed by keyboard; its leaves are what the next letter picks. */
  armedPrefix?: string | null;
  /** The picker owns the keyboard right now, after `B`. */
  isKeyboardActive?: boolean;
  onSelect: (path: string | null) => void;
}> = ({ selectedPath = null, armedPrefix = null, isKeyboardActive = false, onSelect }) => (
  <div
    className={`rr-bucket-inline${isKeyboardActive ? ' is-keyboard-active' : ''}`}
    data-testid="rr-bucket-picker"
  >
    <div className="rr-bucket-inline-head">
      <span className="rr-bucket-title">Primary bucket</span>
      <span className="rr-bucket-copy">
        What single change would most likely have made this sample confident correct?
      </span>
      <button
        type="button"
        data-testid="bucket-picker-clear"
        className="rr-bucket-option is-clear"
        title="Save this verdict with no primary bucket"
        onClick={() => onSelect(null)}
      >
        <span>Clear</span>
        <span className="rr-bucket-key">{BUCKET_PICKER_CLEAR_KEY}</span>
      </button>
    </div>
    <div className="rr-bucket-grid">
      {RECOGNITION_ISSUE_BUCKETS.map((definition) => {
        const isArmed = armedPrefix === definition.path;
        return (
          <section
            key={definition.path}
            data-testid={`bucket-picker-group-${definition.path}`}
            aria-label={definition.label}
            className={`rr-bucket-group${isArmed ? ' is-armed' : ''}${
              armedPrefix && !isArmed ? ' is-dimmed' : ''
            }`}
          >
            <div className="rr-bucket-group-head" title={definition.description}>
              <span className="rr-bucket-group-title">{definition.label}</span>
              <span className="rr-bucket-key">{definition.shortcutKey}</span>
            </div>
            <div className="rr-bucket-owner" title={definition.description}>
              {definition.owner}
              {definition.bucket === RECOGNITION_OTHER_BUCKET ? ' · requires a note' : ''}
            </div>
            <div className="rr-bucket-group-options">
              {definition.subBuckets.map((option) => (
                <button
                  key={option.path}
                  type="button"
                  data-testid={`bucket-picker-option-${option.path}`}
                  title={option.description}
                  className={`rr-bucket-option${selectedPath === option.path ? ' is-selected' : ''}`}
                  onClick={() => onSelect(option.path)}
                >
                  <span>{option.label}</span>
                  <span className="rr-bucket-key">{option.shortcutKey}</span>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  </div>
);

export default RecognitionBucketPicker;
